import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Star, Calendar, TrendingUp, Tv } from 'lucide-react'
import api from '../../services/api'
import toast from 'react-hot-toast'

interface NewShowsWidgetProps {
  widget: any
  isExpanded: boolean
}

interface Show {
  id: number
  title: string
  poster: string
  backdrop: string
  overview: string
  rating: number
  firstAirDate: string
  genre: string[]
  trending: boolean
  status?: string
}

export default function NewShowsWidget({ widget, isExpanded }: NewShowsWidgetProps) {
  const [category, setCategory] = useState<'on_air' | 'upcoming' | 'trending'>('on_air')
  const [selectedShow, setSelectedShow] = useState<Show | null>(null)
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set())

  // Fetch shows from API with 6-hour caching
  const { data: showsData = { on_air: [], upcoming: [], trending: [] } } = useQuery({
    queryKey: ['shows'],
    queryFn: async () => {
      const { data } = await api.get('/shows/all')
      return data
    },
    staleTime: 1000 * 60 * 60 * 6, // 6 hours
  })

  const handleImageError = (src: string) => {
    setImageErrors(prev => new Set([...prev, src]))
  }

  const getFallbackImage = (type: 'poster' | 'backdrop') => {
    if (type === 'poster') {
      return 'https://via.placeholder.com/300x450/667eea/ffffff?text=No+Image'
    }
    return 'https://via.placeholder.com/1280x720/667eea/ffffff?text=No+Image'
  }

  const handleWatchTrailer = (show: Show) => {
    // Search for the show trailer on YouTube
    const searchQuery = encodeURIComponent(`${show.title} ${new Date(show.firstAirDate).getFullYear()} official trailer`)
    window.open(`https://www.youtube.com/results?search_query=${searchQuery}`, '_blank')
  }

  const shows = showsData[category] || []

  const renderCompactView = () => (
    <div className="h-full overflow-y-auto space-y-2 p-4">
      {shows.map((show) => (
        <div
          key={show.id}
          className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-lg transition cursor-pointer"
          onClick={() => {
            if (isExpanded) {
              setSelectedShow(show)
            } else {
              // In compact view, show a helpful message
              toast.info('Click the expand button (⤢) to view show details', {
                duration: 3000,
                position: 'bottom-center'
              })
            }
          }}
          style={{ backgroundColor: 'transparent' }}
        >
          <img
            src={imageErrors.has(show.poster) ? getFallbackImage('poster') : show.poster}
            alt={show.title}
            className="w-16 h-24 object-cover rounded shadow flex-shrink-0"
            onError={() => handleImageError(show.poster)}
          />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm mb-1 line-clamp-2" style={{ color: 'var(--color-text-primary)' }}>{show.title}</div>
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center gap-1">
                <Star size={12} className="text-yellow-500 fill-current" />
                <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{show.rating}</span>
              </div>
              {show.trending && (
                <div className="flex items-center gap-1 text-red-600">
                  <TrendingUp size={12} />
                  <span className="text-xs font-medium">Trending</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              <Calendar size={10} />
              {new Date(show.firstAirDate).toLocaleDateString()}
            </div>
          </div>
        </div>
      ))}
      
      {shows.length === 0 && (
        <div className="text-center py-8" style={{ color: 'var(--color-text-secondary)' }}>
          <Tv size={48} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No shows in this category</p>
        </div>
      )}
    </div>
  )

  const renderExpandedView = () => (
    <div className="h-full flex gap-4 p-4">
      {/* Shows Grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setCategory('on_air')}
            className={`px-4 py-2 rounded-lg text-sm transition ${
              category === 'on_air'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
            style={category !== 'on_air' ? { color: 'var(--color-text-primary)' } : {}}
          >
            On Air
          </button>
          <button
            onClick={() => setCategory('upcoming')}
            className={`px-4 py-2 rounded-lg text-sm transition ${
              category === 'upcoming'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
            style={category !== 'upcoming' ? { color: 'var(--color-text-primary)' } : {}}
          >
            Upcoming
          </button>
          <button
            onClick={() => setCategory('trending')}
            className={`px-4 py-2 rounded-lg text-sm transition flex items-center gap-1 ${
              category === 'trending'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
            style={category !== 'trending' ? { color: 'var(--color-text-primary)' } : {}}
          >
            <TrendingUp size={14} />
            Trending
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {shows.map((show) => (
            <button
              key={show.id}
              onClick={() => setSelectedShow(show)}
              className={`text-left group ${
                selectedShow?.id === show.id ? 'ring-2 ring-primary-500 rounded-lg' : ''
              }`}
            >
              <div className="relative mb-2 rounded-lg overflow-hidden">
                <img
                  src={imageErrors.has(show.poster) ? getFallbackImage('poster') : show.poster}
                  alt={show.title}
                  className="w-full aspect-[2/3] object-cover group-hover:scale-105 transition duration-300"
                  onError={() => handleImageError(show.poster)}
                />
                {show.trending && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-red-600 text-white text-xs rounded-full flex items-center gap-1">
                    <TrendingUp size={10} />
                    Trending
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition" />
              </div>
              <h3 className="font-medium text-sm mb-1 line-clamp-2" style={{ color: 'var(--color-text-primary)' }}>
                {show.title}
              </h3>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <Star size={12} className="text-yellow-500 fill-current" />
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{show.rating}</span>
                </div>
                <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {new Date(show.firstAirDate).getFullYear()}
                </span>
              </div>
            </button>
          ))}
        </div>

        {shows.length === 0 && (
          <div className="text-center py-12" style={{ color: 'var(--color-text-secondary)' }}>
            <Tv size={64} className="mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium mb-2">No Shows Found</p>
            <p className="text-sm">Try selecting a different category</p>
          </div>
        )}
      </div>

      {/* Show Detail */}
      {selectedShow && (
        <div className="w-96 overflow-y-auto border-l pl-4" style={{ borderColor: 'var(--color-border)' }}>
          <div className="relative mb-4 rounded-lg overflow-hidden">
            <img
              src={imageErrors.has(selectedShow.backdrop) ? getFallbackImage('backdrop') : selectedShow.backdrop}
              alt={selectedShow.title}
              className="w-full h-48 object-cover"
              onError={() => handleImageError(selectedShow.backdrop)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <h2 className="text-xl font-bold text-white mb-2">{selectedShow.title}</h2>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <Star size={14} className="text-yellow-500 fill-current" />
                  <span className="text-white font-semibold">{selectedShow.rating}</span>
                </div>
                {selectedShow.trending && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white text-xs rounded-full">
                    <TrendingUp size={10} />
                    Trending
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>First Air Date</h3>
              <div className="flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                <Calendar size={16} />
                {new Date(selectedShow.firstAirDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Genres</h3>
              <div className="flex flex-wrap gap-2">
                {selectedShow.genre.map((g) => (
                  <span
                    key={g}
                    className="px-3 py-1 text-sm rounded-full"
                    style={{ 
                      backgroundColor: 'var(--color-surface)', 
                      color: 'var(--color-text-primary)',
                      border: '1px solid var(--color-border)'
                    }}
                  >
                    {g}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Overview</h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{selectedShow.overview}</p>
            </div>

            <div className="flex gap-2">
              <button 
                onClick={() => handleWatchTrailer(selectedShow)}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex items-center justify-center gap-2"
              >
                <Tv size={16} />
                Watch Trailer
              </button>
              <button 
                className="px-4 py-2 rounded-lg transition"
                style={{ 
                  backgroundColor: 'var(--color-surface)', 
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)'
                }}
                title="Add to watchlist (coming soon)"
              >
                Watchlist
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-hidden">
        {isExpanded ? renderExpandedView() : renderCompactView()}
      </div>
    </div>
  )
}
